import React from "react";
import type { Components } from "react-markdown";

/**
 * Security: Validate URL schemes to prevent XSS via javascript:, data:, vbscript:, etc.
 * Only allow http(s) and relative URLs.
 */
function isSafeUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;

  // Allow relative URLs (starting with / or .)
  if (trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) {
    return true;
  }

  // Allow only http: and https: absolute URLs
  try {
    const parsed = new URL(trimmed, window.location.href);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Safe Markdown components that prevent XSS attacks.
 * - Links: Only allow http(s) and relative URLs, force target=_blank and rel=noopener noreferrer
 * - Images: Only allow http(s) and relative URLs, add loading=lazy
 */
export const safeMarkdownComponents: Components = {
  a: ({ href, children, ...props }) => {
    const safe = isSafeUrl(href);
    if (!safe) return <span className="text-gray-500">{children}</span>;
    return (
      <a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:no-underline"
      >
        {children}
      </a>
    );
  },
  img: ({ src, alt, ...props }) => {
    const safe = isSafeUrl(src);
    if (!safe) return null;
    return (
      <img
        {...props}
        src={src}
        alt={alt || ""}
        loading="lazy"
        className="max-w-full h-auto rounded"
      />
    );
  },
};


